// This file will control all the UI Label shown in cards for all reports on FE

interface CardMetric {
  topLabel: string;
  bottomLabel: string;
  metricDefinition: string; // hover label with metric explanation
  basedOn: string; // tag showing what the metric is based on
  category: string; // metric category (offers, hires, etc.)
}

// Organized by category keys
export const reportCardLabelMapByCategory: Record<
  string,
  Record<string, CardMetric>
> = {
  offers: {
    offer_created: {
      topLabel: 'Offers created',
      bottomLabel: 'OFFERS',
      metricDefinition: 'Number of offers created',
      basedOn: 'Offer creation date',
      category: 'Offers',
    },
    offer_time_to_offer: {
      topLabel: 'Time to offer',
      bottomLabel: 'TIME',
      metricDefinition:
        'Average time taken from application to offer generation',
      basedOn: 'Offer creation date',
      category: 'Offers',
    },
    offer_revision_per_offer: {
      topLabel: 'Avg. revisions per offer',
      bottomLabel: 'OFFER REVISIONS',
      metricDefinition: 'Average number of revisions made to an offer',
      basedOn: 'Offer creation date',
      category: 'Offers',
    },
    accepted_offers: {
      topLabel: 'Offers accepted',
      bottomLabel: 'OFFERS',
      metricDefinition: 'Number of offers accepted',
      basedOn: 'Offer creation date',
      category: 'Offers',
    },
    offer_acceptance_rate: {
      topLabel: 'Offer acceptance rate',
      bottomLabel: 'RATE',
      metricDefinition: 'The percentage of offers accepted',
      basedOn: 'Offer creation date',
      category: 'Offers',
    },
    rejected_offers: {
      topLabel: 'Offers rejected',
      bottomLabel: 'OFFERS',
      metricDefinition: 'Number of offers rejected',
      basedOn: 'Offer creation date',
      category: 'Offers',
    },
  },

  live_job_pipeline: {
    live_job_pipeline_prospect: {
      topLabel: 'Currently in prospect stages',
      bottomLabel: 'APPLICANTS',
      metricDefinition:
        'Number of applicants currently in the prospect (Lead) stages',
      basedOn: 'Current pipeline snapshot',
      category: 'Current job pipeline',
    },
    live_job_pipeline_application_review: {
      topLabel: 'Currently in application review stages',
      bottomLabel: 'APPLICANTS',
      metricDefinition:
        'Number of applicants currently in the application review stages',
      basedOn: 'Current pipeline snapshot',
      category: 'Current job pipeline',
    },
    live_job_pipeline_interviewed: {
      topLabel: 'Currently in interview stages',
      bottomLabel: 'APPLICANTS',
      metricDefinition:
        'Number of applicants currently in the interview stages',
      basedOn: 'Current pipeline snapshot',
      category: 'Current job pipeline',
    },
    live_job_pipeline_offer: {
      topLabel: 'Currently in offer stages',
      bottomLabel: 'APPLICANTS',
      metricDefinition: 'Number of applicants currently in the offer stages',
      basedOn: 'Current pipeline snapshot',
      category: 'Current job pipeline',
    },
    live_job_pipeline_hired: {
      topLabel: 'Currently in hired stages',
      bottomLabel: 'APPLICANTS',
      metricDefinition: 'Number of applicants currently in the hired stages',
      basedOn: 'Current pipeline snapshot',
      category: 'Current job pipeline',
    },
  },

  pipeline: {
    pipeline_applied: {
      topLabel: 'Application review stages',
      bottomLabel: 'APPLICANTS',
      metricDefinition:
        'Number of applicants who reached the application review stage',
      basedOn: 'Application creation date',
      category: 'Pipeline history',
    },
    pipeline_interviewed: {
      topLabel: 'Interview stages',
      bottomLabel: 'APPLICANTS',
      metricDefinition: 'Number of applicants who reached the interview stages',
      basedOn: 'Application creation date',
      category: 'Pipeline history',
    },
    pipeline_offer: {
      topLabel: 'Offer stages',
      bottomLabel: 'APPLICANTS',
      metricDefinition: 'Number of applicants who reached the offer stage',
      basedOn: 'Application creation date',
      category: 'Pipeline history',
    },
    pipeline_hired: {
      topLabel: 'Hired stages',
      bottomLabel: 'APPLICANTS',
      metricDefinition: 'Number of applicants who reached the hired stage',
      basedOn: 'Application creation date',
      category: 'Pipeline history',
    },
  },

  interviews: {
    interviews_completed: {
      topLabel: 'Completed interviews',
      bottomLabel: 'INTERVIEWS',
      metricDefinition: 'Number of interviews that have been completed',
      basedOn: 'Interview date',
      category: 'Interviews',
    },
    applicants_interviewed: {
      topLabel: 'Interviewed applicants',
      bottomLabel: 'APPLICANTS',
      metricDefinition: 'Number of applicants who have been interviewed',
      basedOn: 'Interview date',
      category: 'Interviews',
    },
    time_spent_interviewing: {
      topLabel: 'Avg. time spent interviewing',
      bottomLabel: 'TIME',
      metricDefinition: 'Average time spent interviewing applicants',
      basedOn: 'Interview date',
      category: 'Interviews',
    },
  },

  hires: {
    hires_no_of_hires: {
      topLabel: 'Hires',
      bottomLabel: 'HIRES',
      metricDefinition: 'Number of applicants who have been hired',
      basedOn: 'Hire date',
      category: 'Hires',
    },
    hires_time_to_hire: {
      topLabel: 'Time to hire',
      bottomLabel: 'TIME',
      metricDefinition: 'Average time taken from application to hired',
      basedOn: 'Hire date',
      category: 'Hires',
    },
  },

  rejections: {
    rejections_rejected_candidate: {
      topLabel: 'Rejected candidates',
      bottomLabel: 'CANDIDATES',
      metricDefinition: 'Number of applicants rejected',
      basedOn: 'Rejection date',
      category: 'Rejections',
    },
    rejections_time_to_reject: {
      topLabel: 'Time to reject',
      bottomLabel: 'TIME',
      metricDefinition: 'Average time taken from application to rejection',
      basedOn: 'Rejection date',
      category: 'Rejections',
    },
  },

  contacts: {
    contacts_new: {
      topLabel: 'New contacts',
      bottomLabel: 'Contacts',
      metricDefinition: 'Number of new contacts created',
      basedOn: 'Contact creation date',
      category: 'Contacts',
    },
    contacts_sourced: {
      topLabel: 'Sourced',
      bottomLabel: 'Contacts',
      metricDefinition: 'Number of sourced contacts',
      basedOn: 'Contact creation date',
      category: 'Contacts',
    },
    contacts_inbound: {
      topLabel: 'Inbound',
      bottomLabel: 'Contacts',
      metricDefinition: 'Number of inbound contacts',
      basedOn: 'Contact creation date',
      category: 'Contacts',
    },
    contacts_referral: {
      topLabel: 'Referral',
      bottomLabel: 'Contacts',
      metricDefinition: 'Number of referred contacts',
      basedOn: 'Contact creation date',
      category: 'Contacts',
    },
  },

  jobs: {
    jobs_opened: {
      topLabel: 'Jobs opened',
      bottomLabel: 'JOBS',
      metricDefinition: 'Number of jobs opened',
      basedOn: 'Job open date',
      category: 'Jobs',
    },
    jobs_closed: {
      topLabel: 'Jobs closed',
      bottomLabel: 'JOBS',
      metricDefinition: 'Number of jobs closed',
      basedOn: 'Job open date',
      category: 'Jobs',
    },
    jobs_time_to_close: {
      topLabel: 'Time to close',
      bottomLabel: 'TIME',
      metricDefinition: 'Average time taken to close a job',
      basedOn: 'Job open date',
      category: 'Jobs',
    },
    jobs_target_breached: {
      topLabel: 'Target date breached jobs',
      bottomLabel: 'JOBS',
      metricDefinition:
        'Number of jobs that have stayed open past their target date',
      basedOn: 'Job open date',
      category: 'Jobs',
    },
  },

  recruiter_performance: {
    sourced_candidates: {
      topLabel: 'Sourced applications',
      bottomLabel: 'APPLICANTS',
      metricDefinition: 'Number of applicants added to a job',
      basedOn: 'Application creation date',
      category: 'Recruiter performance',
    },
    screened_candidates: {
      topLabel: 'Screened applications',
      bottomLabel: 'APPLICANTS',
      metricDefinition:
        'Number of applicants screened from the application review stage',
      basedOn: 'Stage movement date',
      category: 'Recruiter performance',
    },
    interviews_scheduled: {
      topLabel: 'Interviews scheduled',
      bottomLabel: 'INTERVIEWS',
      metricDefinition: 'Number of interviews scheduled',
      basedOn: 'Interview scheduled date',
      category: 'Recruiter performance',
    },
    interviews_rescheduled: {
      topLabel: 'Interview reschedules',
      bottomLabel: 'INTERVIEWS',
      metricDefinition: 'Number of reschedules done for interviews',
      basedOn: 'Interview reschedule date',
      category: 'Recruiter performance',
    },
    offers_sent: {
      topLabel: 'Offers sent',
      bottomLabel: 'OFFERS',
      metricDefinition: 'Number of offers sent',
      basedOn: 'Offer creation date',
      category: 'Offers',
    },
    offers_accepted: {
      topLabel: 'Offer acceptance rate',
      bottomLabel: 'ACCEPTANCE RATE',
      metricDefinition: 'The percentage of offers accepted',
      basedOn: 'Offer creation date',
      category: 'Offers',
    },
    hired_candidates: {
      topLabel: 'Hired',
      bottomLabel: 'APPLICANTS',
      metricDefinition: 'Number of applicants who have been hired',
      basedOn: 'Hire date',
      category: 'Hires',
    },
    time_to_hire: {
      topLabel: 'Time to hire',
      bottomLabel: 'TIME',
      metricDefinition: 'Average time taken from application to hired',
      basedOn: 'Hire date',
      category: 'Hires',
    },
    rejected_candidates: {
      topLabel: 'Rejections',
      bottomLabel: 'APPLICANTS',
      metricDefinition: 'Number of applicants rejected',
      basedOn: 'Rejection date',
      category: 'Rejections',
    },
  },
  requisitions: {
    requisitions_opened: {
      topLabel: 'Requisitions opened',
      bottomLabel: 'REQUISITIONS',
      metricDefinition:
        'Number of requisitions marked as open during the selected time period ',
      basedOn: 'Requisition open date',
      category: 'Requisitions',
    },
    requisitions_filled: {
      topLabel: 'Requisitions filled',
      bottomLabel: 'REQUISITIONS',
      metricDefinition:
        'Number of requisitions filled during the selected time period ',
      basedOn: 'Requisition open date',
      category: 'Requisitions',
    },
    requisitions_closed: {
      topLabel: 'Requisitions closed',
      bottomLabel: 'REQUISITIONS',
      metricDefinition:
        'Number of requisitions marked as closed during the selected time period ',
      basedOn: 'Requisition open date',
      category: 'Requisitions',
    },
    total_time_to_fill: {
      topLabel: 'Avg. time to fill',
      bottomLabel: 'TIME',
      metricDefinition:
        'The average time taken to fill requisitions during the selected time period',
      basedOn: 'Requisition open date',
      category: 'Requisitions',
    },
  },
};
